import {
  createAsyncThunk,
  type ThunkDispatch,
  type UnknownAction
} from "@reduxjs/toolkit"
import { type BaseOptionType } from "antd/lib/select"

import { errorSave } from "../../../../redux/errors.slice"
import { type BasePagination } from "../../../../types/api.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import * as services from "../../api/project-finance.services"
import {
  type FinancialTransactionItem,
  type FinancialTransactionSnapshot,
  type FinancialTransactionSummary,
  type Payment
} from "../../types/project-finance.types.tsx"
import { projectSliceName } from "../project.constants.ts"
import { type ProjectFinancialTransactionFilters } from "../project.types.ts"
import { type ProjectFinancialTransactionItemCreateData } from "./project-finance.types.ts"

export const fetchFinancialTransactionItems = createAsyncThunk<
  {
    isMore: boolean
    data: BasePagination<FinancialTransactionItem, FinancialTransactionSummary>
  },
  {
    isMore: boolean
    params: ProjectFinancialTransactionFilters | { project: string }
  }
>(
  `${projectSliceName}/fetchFinancialTransactionItems`,
  async ({ isMore, params }, { dispatch, rejectWithValue }) => {
    const response = await services.getFinancialTransactionItems(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return {
      data: response.data as BasePagination<
        FinancialTransactionItem,
        FinancialTransactionSummary
      >,
      isMore
    }
  }
)

export const exportFinancialTransactionItems = createAsyncThunk<
  Blob,
  { projectId: string; visibleColumns?: string[] }
>(
  `${projectSliceName}/exportFinancialTransactionItems`,
  async ({ projectId, visibleColumns }, { dispatch, rejectWithValue }) => {
    const response = await services.exportFinancialTransactionItems(
      projectId,
      visibleColumns
    )

    if (!response.ok) {
      const errorMessage =
        response?.data instanceof Blob
          ? unknownError
          : response?.data?.details || unknownError

      dispatch(errorSave(errorMessage))
      return rejectWithValue(errorMessage)
    }

    return response.data as Blob
  }
)

export const createFinancialTransactionItem = createAsyncThunk<
  FinancialTransactionItem,
  ProjectFinancialTransactionItemCreateData
>(
  `${projectSliceName}/createFinancialTransactionItem`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createFinancialTransactionItem(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Transaction item created successfully.")

    return (response?.data?.details?.data ??
      response?.data) as FinancialTransactionItem
  }
)

export const patchFinancialTransactionItem = createAsyncThunk<
  FinancialTransactionItem,
  Partial<FinancialTransactionItem> & { id: string }
>(
  `${projectSliceName}/patchFinancialTransactionItem`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.patchFinancialTransactionItem(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Transaction item updated successfully.")
    const res = (response?.data?.details?.data ??
      response?.data) as FinancialTransactionItem

    return { ...res, id: data?.id }
  }
)

export const fetchFinancialTransactionItemsFilterOptions = createAsyncThunk<
  Record<string, BaseOptionType[]>,
  { project_id: string }
>(
  `${projectSliceName}/fetchFinancialTransactionItemsFilterOptions`,
  async (params, { dispatch, rejectWithValue }) => {
    const response =
      await services.getFinancialTransactionItemsFilterOptions(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as Record<string, BaseOptionType[]>
  }
)

export const deleteFinancialTransactionItem = createAsyncThunk<string, string>(
  `${projectSliceName}/deleteFinancialTransactionItem`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteFinancialTransactionItem(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Transaction item deleted successfully.")

    return id
  }
)

export const fetchFinancialSnapshot = createAsyncThunk<
  FinancialTransactionSnapshot,
  string
>(
  `${projectSliceName}/fetchFinancialSnapshot`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getFinancialSnapshot(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as FinancialTransactionSnapshot
  }
)

type ProjectStateReader = () => {
  project: {
    financialSnapshotRequestedFor: string | null
    financialAlertsRequestedFor: string | null
  }
}

// Mount-time readers share one request per project visit; writes call fetchFinancialSnapshot directly.
export const loadFinancialSnapshot =
  (projectId: string) =>
  (
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
    getState: ProjectStateReader
  ) => {
    if (getState().project.financialSnapshotRequestedFor === projectId) return
    return dispatch(fetchFinancialSnapshot(projectId))
  }

export type FinancialAlertsPayload = {
  muted: boolean
  alerts: {
    rule: string
    basis: "planned" | "actual"
    severity: "info" | "warning" | "critical"
    message: string
    recommendation: string
  }[]
}

export const fetchFinancialAlerts = createAsyncThunk<
  FinancialAlertsPayload,
  string
>(
  `${projectSliceName}/fetchFinancialAlerts`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getFinancialAlerts(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as unknown as FinancialAlertsPayload
  }
)

export const loadFinancialAlerts =
  (projectId: string) =>
  (
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
    getState: ProjectStateReader
  ) => {
    if (getState().project.financialAlertsRequestedFor === projectId) return
    return dispatch(fetchFinancialAlerts(projectId))
  }

/**
 * Muting changes only the flag: the engine evaluates the same conditions
 * either way, so the alert list needs no refetch to stay true here.
 */
export const setFinancialAlertsMuted = createAsyncThunk<
  boolean,
  { projectId: string; muted: boolean }
>(
  `${projectSliceName}/setFinancialAlertsMuted`,
  async ({ projectId, muted }, { dispatch, rejectWithValue }) => {
    const response = await services.muteFinancialAlerts(projectId, muted)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return muted
  }
)

type PaymentRejectValue = { status: number; data: unknown }

type CreatePaymentArgs = {
  transactionId: string
  body: {
    amount: string
    payment_method: string
    payment_details?: string
    invoice?: string | null
    payment_date?: string
  }
}

export const createPayment = createAsyncThunk<
  Payment,
  CreatePaymentArgs,
  { rejectValue: PaymentRejectValue }
>(
  `${projectSliceName}/createPayment`,
  async ({ transactionId, body }, { rejectWithValue }) => {
    const response = await services.addPayment(transactionId, body)
    if (!response.ok) {
      return rejectWithValue({
        status: response.status ?? 0,
        data: response.data
      })
    }
    return (response?.data?.details?.data ?? response?.data) as Payment
  }
)

type UpdatePaymentArgs = {
  transactionId: string
  paymentId: string
  body: {
    amount?: string
    payment_method?: string
    payment_details?: string
    invoice?: string | null
    payment_date?: string
  }
}

export const updatePayment = createAsyncThunk<
  Payment,
  UpdatePaymentArgs,
  { rejectValue: PaymentRejectValue }
>(
  `${projectSliceName}/updatePayment`,
  async ({ transactionId, paymentId, body }, { rejectWithValue }) => {
    const response = await services.updatePayment(
      transactionId,
      paymentId,
      body
    )
    if (!response.ok) {
      return rejectWithValue({
        status: response.status ?? 0,
        data: response.data
      })
    }
    return (response?.data?.details?.data ?? response?.data) as Payment
  }
)

type DeletePaymentArgs = {
  transactionId: string
  paymentId: string
}

export const deletePayment = createAsyncThunk<
  string,
  DeletePaymentArgs,
  { rejectValue: PaymentRejectValue }
>(
  `${projectSliceName}/deletePayment`,
  async ({ transactionId, paymentId }, { rejectWithValue }) => {
    const response = await services.deletePayment(transactionId, paymentId)
    if (!response.ok) {
      return rejectWithValue({
        status: response.status ?? 0,
        data: response.data
      })
    }
    return paymentId
  }
)
